import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/content";
import { CASE_STUDIES_PAGE } from "@/constants/testIds";

export default function CaseStudies() {
  const { t } = useTranslation();
  const [featured, ...rest] = CASE_STUDIES;

  return (
    <div className="relative">
      {/* HEADER */}
      <section className="relative pt-24 md:pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_80%_10%,rgba(192,94,76,0.14),transparent_70%)]" />
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("caseStudies.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("caseStudies.heroTitle1")} <span className="italic text-clay-400">{t("caseStudies.heroTitleItalic")}</span>{t("caseStudies.heroTitle2")}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="body-lg mt-10 max-w-2xl">
              {t("caseStudies.heroDescription")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      <section className="pb-16">
        <div className="container-x">
          <Reveal>
            <article
              data-testid={`${CASE_STUDIES_PAGE.cardPrefix}-${featured.slug}`}
              className="card-surface overflow-hidden grid grid-cols-1 md:grid-cols-12 gap-0"
            >
              <div className="md:col-span-7 relative overflow-hidden aspect-[16/10] md:aspect-auto">
                <img src={featured.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-ink-950/20 to-transparent md:bg-gradient-to-r md:from-ink-950/10 md:via-transparent md:to-ink-950/60" />
                <div className="absolute top-6 left-6 text-xs uppercase tracking-widest2 text-clay-400">
                  {t("caseStudies.featured")} · {featured.industry}
                </div>
              </div>
              <div className="md:col-span-5 p-10 md:p-14 flex flex-col justify-center">
                <div className="flex items-baseline gap-4 mb-8">
                  <span className="font-display text-6xl text-clay-400">{featured.metric}</span>
                  <span className="text-xs uppercase tracking-widest2 text-bone-200/50 max-w-[10rem]">
                    {featured.metricLabel}
                  </span>
                </div>
                <h2 className="display-h2 mb-6">{featured.title}</h2>
                <p className="text-bone-200/75 leading-relaxed mb-10">
                  {featured.excerpt}
                </p>
                <Link
                  to={`/case-studies/${featured.slug}`}
                  className="link-arrow text-sm font-semibold uppercase tracking-widest2 self-start"
                  data-testid={`case-featured-cta-${featured.slug}`}
                >
                  {t("caseStudies.discussSimilar")}
                  <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-24 md:pb-32">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {rest.map((c, i) => (
              <Reveal key={c.slug} delay={i * 80}>
                <article
                  data-testid={`${CASE_STUDIES_PAGE.cardPrefix}-${c.slug}`}
                  className="group card-surface overflow-hidden h-full flex flex-col"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={c.image}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                    <div className="absolute top-6 left-6 text-xs uppercase tracking-widest2 text-clay-400">
                      {c.industry}
                    </div>
                  </div>
                  <div className="p-10 flex-1 flex flex-col">
                    <div className="flex items-baseline gap-4 mb-6">
                      <span className="font-display text-5xl text-clay-400">{c.metric}</span>
                      <span className="text-xs uppercase tracking-widest2 text-bone-200/50">
                        {c.metricLabel}
                      </span>
                    </div>
                    <h3 className="display-h3 mb-4">{c.title}</h3>
                    <p className="text-sm text-bone-200/70 leading-relaxed mb-8">
                      {c.excerpt}
                    </p>
                    <Link
                      to={`/case-studies/${c.slug}`}
                      className="link-arrow mt-auto text-sm font-semibold uppercase tracking-widest2"
                      data-testid={`case-card-cta-${c.slug}`}
                    >
                      {t("common.readMore")}
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="border-y border-white/10 bg-ink-900/50 py-16">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { v: "£340M+", l: t("caseStudies.metricsPipeline") },
            { v: "3.8x", l: t("caseStudies.metricsRoas") },
            { v: "62%", l: t("caseStudies.metricsTime") },
            { v: "94%", l: t("caseStudies.metricsRetention") },
          ].map((m) => (
            <div key={m.l}>
              <div className="font-display text-4xl md:text-5xl text-bone-100">{m.v}</div>
              <div className="mt-2 text-xs uppercase tracking-widest2 text-bone-200/50">{m.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="eyebrow justify-center mb-6">{t("caseStudies.ctaEyebrow")}</p>
              <h2 className="display-h2 mb-8">
                {t("caseStudies.ctaTitle")}
              </h2>
              <Link to="/contact" className="btn-primary">
                {t("common.startConversation")}
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
